import { useEffect, useId, useRef, type ReactNode } from 'react';
import { m } from '../paraglide/messages.js';
import { IconButton } from './IconButton.tsx';
import './BottomSheet.css';

const FOCUSABLE =
  'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

/** Where the bottom sheet becomes a centred dialog (BottomSheet.css). */
const DIALOG_QUERY = '(min-width: 1024px)';
/** Dragging the grabber down this far closes the sheet. */
const CLOSE_DRAG_PX = 90;

/**
 * DESIGN.md §10 BottomSheet: grabber · serif title + close · content · optional footer.
 * A modal `<dialog>`: the page behind is inert, focus stays inside (Tab wraps), Esc, a scrim tap,
 * the close button or a swipe down on the grabber closes it. Detents: medium (≈ 55 %) and large.
 */
export function BottomSheet({
  open,
  onClose,
  title,
  detent = 'medium',
  headAction,
  footer,
  children,
}: {
  open: boolean;
  onClose: () => void;
  title: string;
  detent?: 'medium' | 'large';
  /** Replaces the close button in the head (e.g. a ghost "Reset"); Esc and scrim still close. */
  headAction?: ReactNode;
  footer?: ReactNode;
  children: ReactNode;
}) {
  const dialog = useRef<HTMLDialogElement>(null);
  const titleId = useId();
  const drag = useRef<{ startY: number; dy: number } | null>(null);
  const opener = useRef<Element | null>(null);

  useEffect(() => {
    const el = dialog.current;
    if (!el) return;
    if (open && !el.open) {
      opener.current = document.activeElement;
      el.showModal();
      // A field marked data-autofocus takes the focus (else the first control, the close button).
      requestAnimationFrame(() => el.querySelector<HTMLElement>('[data-autofocus]')?.focus());
    } else if (!open && el.open) {
      el.close();
      if (opener.current instanceof HTMLElement) opener.current.focus();
    }
  }, [open]);

  // Unmounting while open (navigation) must not leave the page inert.
  useEffect(() => {
    const el = dialog.current;
    return () => {
      if (el?.open) el.close();
    };
  }, []);

  function onKeyDown(e: React.KeyboardEvent<HTMLDialogElement>) {
    if (e.key !== 'Tab' || !dialog.current) return;
    const items = [...dialog.current.querySelectorAll<HTMLElement>(FOCUSABLE)].filter(
      (el) => el.offsetParent !== null,
    );
    const first = items[0];
    const last = items.at(-1);
    if (!first || !last) return;
    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault();
      first.focus();
    }
  }

  function onPointerDown(e: React.PointerEvent<HTMLDivElement>) {
    // Buttons in the head keep their clicks (pointer capture would retarget them).
    if ((e.target as Element).closest('button, a')) return;
    // From 1024 px the sheet is a centred dialog: nothing to swipe down.
    if (window.matchMedia(DIALOG_QUERY).matches) return;
    drag.current = { startY: e.clientY, dy: 0 };
    e.currentTarget.setPointerCapture(e.pointerId);
  }
  function onPointerMove(e: React.PointerEvent<HTMLDivElement>) {
    if (!drag.current || !dialog.current) return;
    drag.current.dy = Math.max(0, e.clientY - drag.current.startY);
    dialog.current.style.transform = `translateY(${drag.current.dy}px)`;
  }
  function onPointerUp() {
    if (!drag.current || !dialog.current) return;
    const closing = drag.current.dy > CLOSE_DRAG_PX;
    drag.current = null;
    dialog.current.style.transform = '';
    if (closing) onClose();
  }

  return (
    <dialog
      ref={dialog}
      className={`sheet sheet--${detent}`}
      aria-labelledby={titleId}
      onCancel={(e) => {
        e.preventDefault();
        onClose();
      }}
      // A click on the dialog box itself (not its content) is a click on the scrim.
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
      onKeyDown={onKeyDown}
    >
      {open && (
        <div className="sheet__inner">
          <div
            className="sheet__drag"
            onPointerDown={onPointerDown}
            onPointerMove={onPointerMove}
            onPointerUp={onPointerUp}
            onPointerCancel={onPointerUp}
          >
            <div className="sheet__grabber" aria-hidden="true" />
            <div className="sheet__head">
              <h2 className="sheet__title" id={titleId}>
                {title}
              </h2>
              {headAction ?? <IconButton icon="close" label={m.sheet_close()} onClick={onClose} />}
            </div>
          </div>
          <div className="sheet__body">{children}</div>
          {footer && <div className="sheet__foot">{footer}</div>}
        </div>
      )}
    </dialog>
  );
}
